import { useEffect, useState, type ReactNode } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Ban, Check, CheckCheck, CircleX, Eye, Play } from "lucide-react";
import { Button, Modal, ReadField, SectionHeader, SelectField, StatusBadge, Tabs, TextareaField, type Column, type StatusTone } from "@fawkes/ui";
import { formatDate, formatDateTime } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// ── types ────────────────────────────────────────────────────────────────────────────────────────

interface SpeciesCount {
  naam: string | null;
  count: number;
}
interface ExchangeRow {
  id: number;
  status: string;
  direction: "IN" | "OUT";
  counterpartyName: string | null;
  /** The sending shelter's klant id — preset as brenger when launching the receiving-side intake. */
  sourceKlantId: number | null;
  animalCount: number;
  animalNames: string | null;
  /** Distinct species across the exchange's animals (api-supplied) — backs the Diersoort filter. */
  diersoorten: string[] | null;
  firstAnimalId: number | null;
  sentAt: string | null;
  createdDate: string | null;
  rejectionReasonCode: string | null;
  rejectionRemark: string | null;
  targetExternal: boolean;
  /** Species multiplicity, ordered by descending count — backs the Jmix "2× Kat, 1× Hond" cell. */
  speciesCounts: SpeciesCount[] | null;
}
interface AnimalLine {
  lineNumber: number;
  dierId: number | null;
  naam: string | null;
  chipPasnummer: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipRegistrationWizardStatus: string | null;
}
interface PartyContact {
  email: string | null;
  postcode: string | null;
  woonplaats: string | null;
}
interface ExchangeDetail {
  id: number;
  status: string;
  reason: string | null;
  rejectionReasonCode: string | null;
  rejectionRemark: string | null;
  createdDate: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  completedAt: string | null;
  sourceVestigingNaam: string | null;
  sourceKlantNaam: string | null;
  targetKlantNaam: string | null;
  targetVestigingNaam: string | null;
  targetExternal: boolean;
  sourceContact: PartyContact | null;
  targetContact: PartyContact | null;
  animals: AnimalLine[];
}
interface SpeciesOption {
  id: number;
  label: string;
}
interface HistoryEntry {
  id: number;
  eventType: string;
  payload: string | null;
  userId: string | null;
  occurredAt: string | null;
}

/** The Jmix ShelterExchangeService audit vocabulary — known codes get an i18n label. */
const HISTORY_EVENT_TYPES = new Set([
  "EXCHANGE_CREATED",
  "EXCHANGE_SENT",
  "EXCHANGE_ACCEPTED",
  "EXCHANGE_REJECTED",
  "EXCHANGE_CANCELLED",
  "EXCHANGE_COMPLETED",
]);

/** Known chip-registration outcomes; an unknown code renders raw (Jmix chipStatusLabel fallback). */
const CHIP_STATUSES = new Set(["REGISTERED", "NOT_REGISTERED", "SKIPPED_NO_CHIP", "SKIPPED_OUTAGE"]);

// Tone map mirrors the Jmix statusBadgeModifier: SENT=info, ACCEPTED/COMPLETED=success,
// REJECTED/CANCELLED=danger, DRAFT=neutral — so both apps colour each status identically.
const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: "neutral",
  SENT: "info",
  ACCEPTED: "success",
  REJECTED: "danger",
  CANCELLED: "danger",
  COMPLETED: "success",
};

const ALL_STATUSES = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "CANCELLED", "COMPLETED"];
/** The only statuses the inbox ever surfaces (Jmix: fixed option set, not row-derived). */
const INBOX_STATUSES = ["SENT", "ACCEPTED"];

const REJECTION_REASONS = ["WRONG_SHELTER", "ANIMAL_DECLINED", "CAPACITY_FULL", "OPERATOR_RECONSIDERED", "OTHER"];

/** Free-text reject-remark maximum length (Jmix REJECT_REASON_MAX_LENGTH). */
const REJECT_REMARK_MAX_LENGTH = 500;

type Notice = null | "conflict" | "invalid" | "error";

function noticeFor(err: unknown): Notice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 422 ? "invalid" : "error";
}

// ── the screen ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Uitwisselingen (shelter exchanges) inbox/outbox — the React migration of the Jmix
 * {@code ShelterExchangeListView}. Two tabs: Inkomend (exchanges this shelter received, accept/reject)
 * and Uitgaand (exchanges this shelter sent, cancel). A row opens a read-only detail modal. All data is
 * scoped server-side to the CURRENT vestiging ({@code GET /api/exchange/inbox|outbox}) — switching
 * branch in the sidebar changes what both tabs show, like the Jmix list.
 */
export function Uitwisselingen({
  onReceiveIntake,
}: {
  // Launch the receiving-side intake for an accepted exchange. `source` is the sending shelter
  // (klant), preset as the wizard's brenger (Jmix: bringer = ShelterExchange.sourceKlant).
  onReceiveIntake?: (
    animalId: string,
    exchangeId: string,
    source: { id: number | null; label: string | null },
  ) => void;
} = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState<"inbox" | "outbox">("inbox");
  const [fStatus, setFStatus] = useState("");
  const [fAsiel, setFAsiel] = useState("");
  const [fSoort, setFSoort] = useState("");
  const [detailId, setDetailId] = useState<number | null>(null);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectCode, setRejectCode] = useState("");
  const [rejectRemark, setRejectRemark] = useState("");
  const [cancelId, setCancelId] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [toast, setToast] = useState<string | null>(null);

  const inbox = useCustom<ExchangeRow[]>({ url: "exchange/inbox", method: "get" });
  const outbox = useCustom<ExchangeRow[]>({ url: "exchange/outbox", method: "get" });
  const rows = (tab === "inbox" ? inbox.data?.data : outbox.data?.data) ?? [];
  const refetchBoth = () => { inbox.refetch(); outbox.refetch(); };

  // Full species catalogue for the Diersoort filter (Jmix offers every Diersoort, not just the
  // species present in the loaded rows).
  const speciesRef = useCustom<SpeciesOption[]>({ url: "animals/reference/species", method: "get" });
  const speciesCatalogue = Array.isArray(speciesRef.data?.data) ? speciesRef.data.data : [];

  const detail = useCustom<ExchangeDetail>({
    url: `exchange/${detailId}`,
    method: "get",
    queryOptions: { enabled: detailId != null },
  });
  const detailData = detail.data?.data;

  // Geschiedenis: the exchange's audit-event timeline (Jmix detail's history section).
  const history = useCustom<HistoryEntry[]>({
    url: `exchange/${detailId}/history`,
    method: "get",
    queryOptions: { enabled: detailId != null },
  });
  const historyData = Array.isArray(history.data?.data) ? history.data.data : [];
  const refetchDetail = () => { detail.refetch(); history.refetch(); };

  const { mutate, isLoading: busy } = useCustomMutation();

  // Success feedback (Jmix Notification.show parity) — transient, auto-clears.
  useEffect(() => {
    if (toast == null) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const runAction = (id: number, action: "accept" | "cancel" | "complete", after?: () => void) => {
    setNotice(null);
    mutate(
      { url: `exchange/${id}/${action}`, method: "post", values: {} },
      {
        onSuccess: () => {
          refetchBoth();
          if (detailId === id) refetchDetail();
          after?.();
        },
        onError: (e) => setNotice(noticeFor(e)),
      },
    );
  };

  // The sending shelter as intake-wizard preset brenger. Inbox rows only — there the counterparty
  // IS the source shelter (direction=IN), matching the api's sourceKlantId.
  const intakeSource = (r: ExchangeRow) => ({
    id: r.sourceKlantId,
    label: tab === "inbox" ? r.counterpartyName : null,
  });

  // Jmix parity: accepting an exchange immediately launches the receiving-side intake wizard for it
  // (ShelterExchangeListView.performAccept → ShelterIntake prepop). The "Inboeken" action on an
  // already-ACCEPTED row stays as the re-entry path.
  const acceptExchange = (r: ExchangeRow) => {
    runAction(r.id, "accept", () => {
      setToast(t("uitwisselingen.notif.accepted"));
      if (onReceiveIntake && r.firstAnimalId != null) {
        onReceiveIntake(String(r.firstAnimalId), String(r.id), intakeSource(r));
      }
    });
  };

  const confirmCancel = () => {
    if (cancelId == null) return;
    const id = cancelId;
    setCancelId(null);
    runAction(id, "cancel", () => setToast(t("uitwisselingen.notif.cancelled")));
  };

  const submitReject = () => {
    if (rejectId == null || !rejectCode) return;
    setNotice(null);
    mutate(
      { url: `exchange/${rejectId}/reject`, method: "post", values: { code: rejectCode, remark: rejectRemark || null } },
      {
        onSuccess: () => {
          setRejectId(null); setRejectCode(""); setRejectRemark("");
          setToast(t("uitwisselingen.notif.rejected"));
          refetchBoth(); refetchDetail();
        },
        onError: (e) => setNotice(noticeFor(e)),
      },
    );
  };

  // Perspective label (Jmix statusLabel): viewed from the inbox the receiving shelter never "sent"
  // the exchange, so SENT reads as "Ontvangen" there.
  const statusLabel = (status: string, perspective: "inbox" | "outbox") =>
    perspective === "inbox" && status === "SENT"
      ? t("uitwisselingen.status.SENT_INBOX")
      : t(`uitwisselingen.status.${status}`);

  const statusBadge = (status: string, perspective: "inbox" | "outbox") => (
    <StatusBadge tone={STATUS_TONE[status] ?? "neutral"}>{statusLabel(status, perspective)}</StatusBadge>
  );

  // Status cell: the badge, plus — for a rejected exchange — the operator's first question ("why?")
  // as a muted sub-line and the optional free-text remark as the cell tooltip (Jmix buildStatusBadge).
  const statusCell = (r: ExchangeRow) => {
    const badge = statusBadge(r.status, tab);
    if (r.status !== "REJECTED" || !r.rejectionReasonCode) return badge;
    return (
      <div className="fawkes-exchange__status-cell" title={r.rejectionRemark?.trim() || undefined}>
        {badge}
        <span className="fawkes-exchange__status-reason">{t(`uitwisselingen.reason.${r.rejectionReasonCode}`)}</span>
      </div>
    );
  };

  // Dieren cell (Jmix buildAnimalsCell): prominent count line + muted species breakdown.
  const animalsCell = (r: ExchangeRow) => {
    const count = r.animalCount;
    const species = (r.speciesCounts ?? [])
      .map((s) => `${s.count}× ${s.naam ?? t("uitwisselingen.animals.unknownSpecies")}`)
      .join(", ");
    return (
      <div className="fawkes-exchange__animals-cell">
        <span className="fawkes-exchange__animals-count">
          {count === 0 ? "—" : `${count} ${t(count === 1 ? "uitwisselingen.animals.one" : "uitwisselingen.animals.many")}`}
        </span>
        {count > 0 && species && <span className="fawkes-exchange__animals-species">{species}</span>}
      </div>
    );
  };

  // Counterparty cell: on Uitgaand an out-of-organisation target carries the "Extern" tag telling the
  // operator not to wait for an in-app response (notification goes by e-mail).
  const counterpartyCell = (r: ExchangeRow) => {
    const name = r.counterpartyName || "—";
    if (tab !== "outbox" || !r.targetExternal) return name;
    return (
      <div className="fawkes-exchange__target-cell">
        <span>{name}</span>
        <span className="fawkes-exchange__extern-tag" title={t("uitwisselingen.external.tooltip")}>
          {t("uitwisselingen.external.label")}
        </span>
      </div>
    );
  };

  // Icon-only row-action button (Jmix actionButton): compact square, label as tooltip + aria-label.
  const actionButton = (label: string, icon: ReactNode, onClick: () => void, mod?: "primary" | "danger") => (
    <button
      type="button"
      className={"fawkes-exchange__action-btn" + (mod ? ` fawkes-exchange__action-btn--${mod}` : "")}
      title={label}
      aria-label={label}
      disabled={busy}
      onClick={onClick}
    >
      {icon}
    </button>
  );

  const rowActions = (r: ExchangeRow) => (
    <div className="fawkes-exchange__row-actions">
      {actionButton(t("uitwisselingen.action.open"), <Eye size={16} aria-hidden="true" />, () => setDetailId(r.id))}
      {tab === "inbox" && r.status === "SENT" && (
        <>
          {actionButton(t("uitwisselingen.action.accept"), <Check size={16} aria-hidden="true" />, () => acceptExchange(r), "primary")}
          {actionButton(t("uitwisselingen.action.reject"), <CircleX size={16} aria-hidden="true" />, () => {
            setRejectId(r.id); setRejectCode(""); setRejectRemark("");
          }, "danger")}
        </>
      )}
      {tab === "outbox" && (r.status === "DRAFT" || r.status === "SENT") && (
        actionButton(t("uitwisselingen.action.cancel"), <Ban size={16} aria-hidden="true" />, () => setCancelId(r.id), "danger")
      )}
      {/* Receiving-side intake of an accepted exchange — completing the intake completes the exchange. */}
      {r.status === "ACCEPTED" && onReceiveIntake && r.firstAnimalId != null && (
        actionButton(t("uitwisselingen.action.intake"), <Play size={16} aria-hidden="true" />, () =>
          onReceiveIntake(String(r.firstAnimalId), String(r.id), intakeSource(r)), "primary")
      )}
      {/* Finalise an accepted exchange directly (manual fallback to the receiving intake). */}
      {r.status === "ACCEPTED" && (
        actionButton(t("uitwisselingen.action.complete"), <CheckCheck size={16} aria-hidden="true" />, () =>
          runAction(r.id, "complete", () => setToast(t("uitwisselingen.notif.completed"))))
      )}
    </div>
  );

  // Column order = Jmix reorderColumns: Status · Dieren · Bron/Doel-asiel · Verzonden op · Acties.
  const columns: Column<ExchangeRow>[] = [
    { header: t("uitwisselingen.col.status"), cell: statusCell },
    { header: t("uitwisselingen.col.animals"), cell: animalsCell },
    {
      header: t(tab === "inbox" ? "uitwisselingen.col.source" : "uitwisselingen.col.target"),
      cell: counterpartyCell,
    },
    {
      header: t(tab === "inbox" ? "uitwisselingen.col.sentAt" : "uitwisselingen.col.createdAt"),
      cell: (r) => formatDateTime(tab === "inbox" ? r.sentAt : r.createdDate, locale) || "—",
    },
    { header: t("uitwisselingen.col.actions"), cell: rowActions },
  ];

  const loading = tab === "inbox" ? inbox.isLoading : outbox.isLoading;

  // Filter options. Status is the fixed per-tab vocabulary (Jmix configureStatusOptions); Diersoort is
  // the full catalogue; the Asiel party list is derived from the tab's rows (they are loaded
  // unfiltered, so this matches Jmix's distinct-counterparty query).
  const statusOptions = tab === "inbox" ? INBOX_STATUSES : ALL_STATUSES;
  const asielOptions = [...new Set(rows.map((r) => r.counterpartyName).filter((n): n is string => !!n))].sort();
  const soortOptions = speciesCatalogue.map((s) => s.label).filter(Boolean);
  const filteredRows = rows.filter(
    (r) =>
      (!fStatus || r.status === fStatus) &&
      (!fAsiel || r.counterpartyName === fAsiel) &&
      (!fSoort || (r.diersoorten ?? []).includes(fSoort)),
  );
  const hasActiveFilter = !!(fStatus || fAsiel || fSoort);
  const switchTab = (k: "inbox" | "outbox") => {
    setTab(k);
    setFStatus(""); setFAsiel(""); setFSoort("");
  };

  // Tab labels carry the tab's unfiltered total; when a filter narrows the ACTIVE tab it shows
  // "matched/total" (Jmix refreshTabLabels: "Inkomend (3/12)").
  const tabLabel = (key: "inbox" | "outbox") => {
    const base = t(`uitwisselingen.tab.${key}`);
    const total = (key === "inbox" ? inbox.data?.data : outbox.data?.data)?.length ?? 0;
    if (key === tab && hasActiveFilter && filteredRows.length < total) {
      return `${base} (${filteredRows.length}/${total})`;
    }
    return total > 0 ? `${base} (${total})` : base;
  };

  return (
    <div className="fawkes-exchange">
      <div className="fawkes-exchange__header">
        <SectionHeader>{t("uitwisselingen.heading")}</SectionHeader>
        <p className="fawkes-exchange__subtitle fawkes-muted">{t("uitwisselingen.subtitle")}</p>
      </div>
      <Tabs
        ariaLabel={t("uitwisselingen.title")}
        activeKey={tab}
        onChange={(k) => switchTab(k as "inbox" | "outbox")}
        tabs={[
          { key: "inbox", label: tabLabel("inbox") },
          { key: "outbox", label: tabLabel("outbox") },
        ]}
      />
      <div className="fawkes-exchange__filters">
        <SelectField
          label={t("uitwisselingen.filter.status")}
          value={fStatus}
          onChange={(e) => setFStatus(e.target.value)}
          options={[
            { value: "", label: t("uitwisselingen.filter.status.all") },
            ...statusOptions.map((s) => ({ value: s, label: statusLabel(s, tab) })),
          ]}
        />
        <SelectField
          label={t("uitwisselingen.filter.asiel")}
          value={fAsiel}
          onChange={(e) => setFAsiel(e.target.value)}
          options={[
            { value: "", label: t("uitwisselingen.filter.asiel.all") },
            ...asielOptions.map((a) => ({ value: a, label: a })),
          ]}
        />
        <SelectField
          label={t("uitwisselingen.filter.diersoort")}
          value={fSoort}
          onChange={(e) => setFSoort(e.target.value)}
          options={[
            { value: "", label: t("uitwisselingen.filter.diersoort.all") },
            ...soortOptions.map((s) => ({ value: s, label: s })),
          ]}
        />
      </div>
      {toast && (
        <div className="fawkes-exchange__toast" role="status">{toast}</div>
      )}
      {notice && (
        <div className={`fawkes-inboeken__notice fawkes-inboeken__notice--${notice}`} role="alert">
          {t(`uitwisselingen.notice.${notice}`)}
        </div>
      )}
      {loading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : (
        <IndeelbareTabel tabelKey="uitwisselingen-1"
          columns={columns}
          rows={filteredRows}
          getRowKey={(r) => String(r.id)}
          empty={<p className="fawkes-muted">{t("uitwisselingen.empty")}</p>}
        />
      )}

      {/* detail modal */}
      <Modal
        open={detailId != null}
        title={t("uitwisselingen.detail.title")}
        onClose={() => setDetailId(null)}
        footer={
          <>
            {/* Source-side withdrawal from the detail (Jmix detail's cancel button): outbox
                perspective only, while the exchange is still DRAFT/SENT. Same confirm dialog. */}
            {tab === "outbox" && detailData && (detailData.status === "DRAFT" || detailData.status === "SENT") && (
              <Button className="fawkes-exchange__btn-danger" onClick={() => setCancelId(detailData.id)} disabled={busy}>
                {t("uitwisselingen.action.cancel")}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDetailId(null)}>{t("common.close")}</Button>
          </>
        }
      >
        {detailData ? (
          <div className="fawkes-exchange__detail">
            <div className="fawkes-exchange__detail-head">
              {statusBadge(detailData.status, tab)}
              {detailData.targetExternal && (
                <StatusBadge tone="neutral">{t("uitwisselingen.detail.external")}</StatusBadge>
              )}
              <span className="fawkes-exchange__detail-id">#{detailData.id}</span>
            </div>
            {/* Parties — source + target panels with the Jmix contact line (email · postcode woonplaats). */}
            <div className="fawkes-exchange__parties">
              {(
                [
                  { label: t("uitwisselingen.col.source"), name: detailData.sourceVestigingNaam || detailData.sourceKlantNaam, contact: detailData.sourceContact },
                  { label: t("uitwisselingen.col.target"), name: detailData.targetVestigingNaam || detailData.targetKlantNaam, contact: detailData.targetContact },
                ] as const
              ).map((p) => (
                <div className="fawkes-exchange__party" key={p.label}>
                  <span className="fawkes-exchange__party-label">{p.label}</span>
                  <span className="fawkes-exchange__party-name">{p.name || "—"}</span>
                  {p.contact && (p.contact.email || p.contact.postcode || p.contact.woonplaats) && (
                    <span className="fawkes-exchange__party-details">
                      {[p.contact.email, [p.contact.postcode, p.contact.woonplaats].filter(Boolean).join(" ")]
                        .filter((s) => s && s.trim() !== "")
                        .join(" · ")}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <dl className="fawkes-exchange__fields">
              <ReadField layout="inline" label={t("uitwisselingen.detail.reason")} value={detailData.reason || t("uitwisselingen.detail.reason.empty")} />
              <ReadField layout="inline" label={t("uitwisselingen.detail.createdAt")} value={formatDateTime(detailData.createdDate, locale)} />
              <ReadField layout="inline" label={t("uitwisselingen.col.sentAt")} value={formatDateTime(detailData.sentAt, locale)} />
              {detailData.acceptedAt && <ReadField layout="inline" label={t("uitwisselingen.detail.acceptedAt")} value={formatDateTime(detailData.acceptedAt, locale)} />}
              {detailData.rejectedAt && <ReadField layout="inline" label={t("uitwisselingen.detail.rejectedAt")} value={formatDateTime(detailData.rejectedAt, locale)} />}
              {detailData.rejectionReasonCode && (
                <ReadField layout="inline" label={t("uitwisselingen.detail.rejectionReason")} value={t(`uitwisselingen.reason.${detailData.rejectionReasonCode}`)} />
              )}
              {detailData.rejectionRemark && (
                <ReadField layout="inline" label={t("uitwisselingen.detail.rejectionRemark")} value={detailData.rejectionRemark} />
              )}
              {detailData.cancelledAt && <ReadField layout="inline" label={t("uitwisselingen.detail.cancelledAt")} value={formatDateTime(detailData.cancelledAt, locale)} />}
              {detailData.completedAt && <ReadField layout="inline" label={t("uitwisselingen.detail.completedAt")} value={formatDateTime(detailData.completedAt, locale)} />}
            </dl>
            {/* Animal grid — the Jmix detail's column set, count in the section title. */}
            <span className="fui-field__label">{`${t("uitwisselingen.detail.animals")} (${detailData.animals.length})`}</span>
            <IndeelbareTabel tabelKey="uitwisselingen-2"
              columns={[
                { header: "#", cell: (a: AnimalLine) => String(a.lineNumber) },
                { header: t("animalListView.column.chip"), cell: (a: AnimalLine) => a.chipPasnummer || "—" },
                { header: t("animalListView.column.naam"), cell: (a: AnimalLine) => a.naam ?? `#${a.dierId}` },
                { header: t("animalListView.column.diersoort"), cell: (a: AnimalLine) => a.diersoort || "—" },
                { header: t("animalListView.column.ras"), cell: (a: AnimalLine) => a.ras || "—" },
                { header: t("animalListView.column.geslacht"), cell: (a: AnimalLine) => a.geslacht || "—" },
                { header: t("animalListView.column.geboortedatum"), cell: (a: AnimalLine) => formatDate(a.geboortedatum, locale) || "—" },
                {
                  header: t("uitwisselingen.detail.col.chipStatus"),
                  cell: (a: AnimalLine) =>
                    a.chipRegistrationWizardStatus
                      ? (CHIP_STATUSES.has(a.chipRegistrationWizardStatus)
                          ? t(`uitwisselingen.chipStatus.${a.chipRegistrationWizardStatus}`)
                          : a.chipRegistrationWizardStatus)
                      : "—",
                },
              ]}
              rows={detailData.animals}
              getRowKey={(a) => String(a.lineNumber)}
              empty={<p className="fawkes-muted">—</p>}
            />
            {/* Geschiedenis — the audit-event timeline, same event cards as the Relaties history tab. */}
            <span className="fui-field__label">{t("uitwisselingen.detail.history")}</span>
            {historyData.length === 0 ? (
              <p className="fawkes-muted">{t("uitwisselingen.history.empty")}</p>
            ) : (
              <div className="fawkes-history__items">
                {historyData.map((e) => (
                  <div className="fawkes-relatie-history__event" key={e.id}>
                    <span className="fawkes-relatie-history__title">
                      {HISTORY_EVENT_TYPES.has(e.eventType)
                        ? t(`uitwisselingen.history.${e.eventType}`)
                        : e.eventType.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                    {e.payload && e.payload.trim() !== "" && (
                      <span className="fawkes-relatie-history__payload">{e.payload}</span>
                    )}
                    <span className="fawkes-relatie-history__meta">
                      {formatDateTime(e.occurredAt, locale)}
                      {e.userId ? ` · ${e.userId}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="fawkes-muted">{t("common.loading")}</p>
        )}
      </Modal>

      {/* cancel confirm — Jmix ConfirmDialog parity: cancelling is irreversible, so it never fires
          straight from the row button. */}
      <Modal
        open={cancelId != null}
        title={t("uitwisselingen.cancel.title")}
        onClose={() => setCancelId(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelId(null)}>{t("uitwisselingen.cancel.back")}</Button>
            <Button className="fawkes-exchange__btn-danger" onClick={confirmCancel} disabled={busy}>
              {t("uitwisselingen.cancel.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("uitwisselingen.cancel.warning")}</p>
      </Modal>

      {/* reject dialog */}
      <Modal
        open={rejectId != null}
        title={t("uitwisselingen.reject.title")}
        onClose={() => setRejectId(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectId(null)}>{t("common.cancel")}</Button>
            <Button className="fawkes-exchange__btn-danger" onClick={submitReject} disabled={!rejectCode || busy}>
              {t("uitwisselingen.action.reject")}
            </Button>
          </>
        }
      >
        <SelectField
          label={t("uitwisselingen.reject.reason")}
          value={rejectCode}
          onChange={(e) => setRejectCode(e.target.value)}
          options={[
            { value: "", label: t("uitwisselingen.reject.reasonPlaceholder") },
            ...REJECTION_REASONS.map((r) => ({ value: r, label: t(`uitwisselingen.reason.${r}`) })),
          ]}
        />
        <p className="fawkes-exchange__field-helper fawkes-muted">{t("uitwisselingen.reject.codeHelper")}</p>
        <TextareaField
          label={t("uitwisselingen.reject.remark")}
          rows={3}
          maxLength={REJECT_REMARK_MAX_LENGTH}
          value={rejectRemark}
          onChange={(e) => setRejectRemark(e.target.value)}
        />
        <p className="fawkes-exchange__field-helper fawkes-muted">{t("uitwisselingen.reject.remarkHelper")}</p>
      </Modal>
    </div>
  );
}
